import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
  ],
};

const allServices = [
  {
    slug: "sell-property",
    icon: "ri-home-gear-line",
    title: "Sell Property",
    desc: "Price discovery, verified buyer pools and closure management.",
    link: "/services/sell-property",
  },
  {
    slug: "lease-property",
    icon: "ri-file-list-3-line",
    title: "Lease Property",
    desc: "Tenant sourcing, rent benchmarking and lease structuring.",
    link: "/services/leasing",
  },
  {
    slug: "joint-venture",
    icon: "ri-shake-hands-line",
    title: "Joint Venture",
    desc: "JV structures, partner vetting and revenue-share modelling.",
    link: "/services/jda",
  },
  {
    slug: "redevelopment",
    icon: "ri-hammer-line",
    title: "Redevelopment",
    desc: "FSI study, society consent support and developer bidding.",
    link: "/services/redevelopment",
  },
  {
    slug: "investment",
    icon: "ri-line-chart-line",
    title: "Investment",
    desc: "Curated land and income assets for investor mandates.",
    link: "/contact",
  },
  {
    slug: "valuation",
    icon: "ri-scales-3-line",
    title: "Valuation",
    desc: "Bank-grade valuation with development potential upside.",
    link: "/services/valuation",
  },
  {
    slug: "legal",
    icon: "ri-shield-check-line",
    title: "Legal",
    desc: "Title diligence, conversions, approvals and documentation.",
    link: "/services/legal",
  },
  {
    slug: "property-management",
    icon: "ri-settings-3-line",
    title: "Property Management",
    desc: "Rent, compliance, upkeep and owner reporting.",
    link: "/contact",
  },
  {
    slug: "construction",
    icon: "ri-tools-line",
    title: "Construction",
    desc: "PMC, contractor selection and execution monitoring.",
    link: "/services/construction",
  },
  {
    slug: "digital-marketing",
    icon: "ri-megaphone-line",
    title: "Digital Marketing",
    desc: "Performance campaigns targeting real buyer intent.",
    link: "/services/digital-marketing",
  },
];

const coreStrategies = [
  {
    slug: "jda",
    icon: "ri-building-2-line",
    title: "Joint Development (JDA)",
    desc: "Partner with builders for apartments or villas. You get a share of constructed area + upfront cash. Zero investment required.",
    badge: "Best for High Value Land",
    badgeColor: "bg-primary-100 text-primary-800",
    link: "/services/jda",
    image:
      "https://readdy.ai/api/search-image?query=Modern%20multi-story%20residential%20building%20under%20construction%20in%20India%2C%20cranes%20and%20scaffolding%2C%20golden%20hour%20lighting%2C%20clean%20editorial%20photography%20style%2C%20warm%20neutral%20tones%2C%20professional%20real%20estate%20development%20scene&width=800&height=600&seq=jda-strategy&orientation=landscape",
  },
  {
    slug: "leasing",
    icon: "ri-time-line",
    title: "Commercial & Long-Term Leasing",
    desc: "Warehousing, Dark Stores, Data Centers, or EV Charging Hubs. Stable, 9-year+ recurring rental income.",
    badge: "Best for Recurring Income",
    badgeColor: "bg-secondary-100 text-secondary-800",
    link: "/services/leasing",
    image:
      "https://readdy.ai/api/search-image?query=Modern%20industrial%20warehouse%20complex%20with%20logistics%20trucks%2C%20clean%20concrete%20architecture%2C%20blue%20sky%20background%2C%20editorial%20photography%20style%2C%20warm%20neutral%20tones%2C%20professional%20commercial%20real%20estate&width=800&height=600&seq=leasing-strategy&orientation=landscape",
  },
  {
    slug: "franchise",
    icon: "ri-store-line",
    title: "Franchise & Retail Outlet Setup",
    desc: "Drive-thrus, Fuel Stations, Supermarkets, QSR chains. High-visibility roadside plots turned into brand-name revenue generators.",
    badge: "Best for Roadside Plots",
    badgeColor: "bg-primary-100 text-primary-800",
    link: "/services/franchise",
    image:
      "https://readdy.ai/api/search-image?query=Modern%20fast%20food%20drive-thru%20restaurant%20exterior%20with%20branded%20signage%2C%20clean%20retail%20architecture%2C%20paved%20roadside%20plot%2C%20editorial%20photography%20style%2C%20warm%20neutral%20tones%2C%20professional%20commercial%20real%20estate&width=800&height=600&seq=franchise-strategy&orientation=landscape",
  },
  {
    slug: "land-as-equity",
    icon: "ri-leaf-line",
    title: '"Land-as-Equity" Consulting',
    desc: "Eco-resorts, Glamping, Solar Farms, Agritourism. Convert dormant scenic or remote land into sustainable business equity.",
    badge: "Best for Scenic/Remote",
    badgeColor: "bg-primary-100 text-primary-800",
    link: "/services/land-as-equity",
    image:
      "https://readdy.ai/api/search-image?query=Luxury%20eco-resort%20with%20canvas%20glamping%20tents%20on%20green%20rolling%20hills%2C%20natural%20landscape%20with%20mountains%20in%20background%2C%20golden%20hour%20lighting%2C%20editorial%20photography%20style%2C%20warm%20neutral%20tones%2C%20sustainable%20tourism%20real%20estate&width=800&height=600&seq=equity-strategy&orientation=landscape",
  },
];

export default function ServicesPage() {
  usePageMeta({
    title: "Land Advisory Services — Sell, Lease, JV, Redevelop & More | CashMyProperty.ai",
    description:
      "10 end-to-end real estate advisory services: sell property, lease placement, joint ventures, redevelopment, investment, valuation, legal, property management, construction, and digital marketing. Advisory that ends in execution.",
    canonicalUrl: "https://cashmyproperty.ai/services",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-sm text-foreground-500"
          >
            <Link
              to="/"
              className="hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line mr-1" />
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Services</span>
          </nav>
        </div>
      </section>

      {/* Dark Services Grid */}
      <section className="py-20 md:py-28 bg-background-950">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-400 mb-4">
              Services
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground-50 leading-tight">
              Advisory that ends in execution
            </h1>
          </div>

          {/* 10-Card Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5 max-w-7xl mx-auto">
            {allServices.map((s) => (
              <Link
                key={s.slug}
                to={s.link}
                className="group relative rounded-2xl border border-foreground-800/30 bg-foreground-900/20 p-5 md:p-6 hover:border-primary-500/40 hover:bg-foreground-900/40 transition-all duration-300 flex flex-col"
              >
                {/* Icon */}
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 border border-primary-500/20 mb-5 group-hover:bg-primary-500/20 transition-colors">
                  <i className={`${s.icon} text-lg text-primary-400`} />
                </div>

                {/* Title */}
                <h3 className="font-display font-semibold text-base text-foreground-100 mb-2 group-hover:text-primary-300 transition-colors">
                  {s.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-foreground-400 leading-relaxed">{s.desc}</p>

                {/* Hover arrow */}
                <div className="mt-auto pt-4 flex items-center gap-1 text-xs text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more{" "}
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Core Monetization Strategies — 2 column cards with images */}
      <section className="py-20 md:py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Deep Dives
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-foreground-950 mb-3">
              Explore Our Core Monetization Strategies
            </h2>
            <p className="text-foreground-600">
              Detailed guides on how each strategy works, step-by-step processes, and real
              outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {coreStrategies.map((s) => (
              <Link
                key={s.slug}
                to={s.link}
                className="group rounded-2xl bg-background-100 border border-background-200 hover:border-primary-300 transition-all duration-300 overflow-hidden flex flex-col"
              >
                {/* Image */}
                <div className="relative w-full aspect-[4/3] overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </div>

                {/* Content */}
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl bg-primary-500/10 border border-primary-500/20 group-hover:bg-primary-500/20 transition-colors">
                      <i className={`${s.icon} text-xl text-primary-500`} />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-lg md:text-xl text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {s.title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4 flex-1">
                    {s.desc}
                  </p>

                  <div className="flex items-center justify-between mt-auto">
                    <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full ${s.badgeColor}`}>
                      {s.badge}
                    </span>
                    <span className="text-sm text-primary-600 font-medium flex items-center gap-1">
                      Read details
                      <i className="ri-arrow-right-line text-primary-500 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Not Sure Which Service Fits Your Land?
          </h2>
          <p className="text-foreground-600 mb-8">
            Our AI Feasibility Report analyzes your plot and recommends the top 3 monetization
            paths — ranked by ROI.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Get My Feasible AI Report
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}